/**
 * Dashboard chart hover layer.
 *
 * The charts are rendered server-side as SVG, so they are readable with
 * JavaScript disabled — every value is in the axis labels and the "View as
 * table" twin. This module only adds the hover/focus readout on top.
 *
 * The hit target is a full-width band per period, not the ~18px bar, so the
 * reader aims at a month rather than at a mark. Focus is wired alongside hover
 * so keyboard users get the same readout.
 */

const TOOLTIP_OFFSET = 12;

function readValues(band) {
    return {
        period: band.dataset.vizPeriod || '',
        quoted: band.dataset.vizQuoted || '',
        invoiced: band.dataset.vizInvoiced || '',
    };
}

function showTooltip(chart, band) {
    const tooltip = chart.querySelector('[data-viz-tooltip]');
    if (!tooltip) return;

    const values = readValues(band);
    const periodEl = tooltip.querySelector('[data-viz-tooltip-period]');

    // Labels come from model data, so they are inserted as text, never as HTML.
    if (periodEl) periodEl.textContent = values.period;
    tooltip.querySelectorAll('[data-viz-tooltip-value]').forEach((node) => {
        const key = node.getAttribute('data-viz-tooltip-value');
        node.textContent = key === 'quoted' ? values.quoted : values.invoiced;
    });

    tooltip.hidden = false;
    tooltip.setAttribute('data-open', 'true');

    // Position against the band's on-screen box, which already accounts for the
    // SVG's own scaling inside its scroll container.
    const chartBox = chart.getBoundingClientRect();
    const bandBox = band.getBoundingClientRect();
    const tipBox = tooltip.getBoundingClientRect();

    let left = bandBox.left - chartBox.left + bandBox.width / 2 - tipBox.width / 2;
    left = Math.max(0, Math.min(left, chartBox.width - tipBox.width));

    let top = bandBox.top - chartBox.top - tipBox.height - TOOLTIP_OFFSET;
    if (top < 0) {
        // Not enough room above: sit below the pointer position instead of
        // clipping outside the card.
        top = bandBox.top - chartBox.top + TOOLTIP_OFFSET;
    }

    tooltip.style.left = `${Math.round(left)}px`;
    tooltip.style.top = `${Math.round(top)}px`;
}

function hideTooltip(chart) {
    const tooltip = chart.querySelector('[data-viz-tooltip]');
    if (!tooltip) return;
    tooltip.setAttribute('data-open', 'false');
    tooltip.hidden = true;
}

function initChart(chart) {
    if (chart.dataset.vizReady === 'true') return;
    chart.dataset.vizReady = 'true';

    const bands = chart.querySelectorAll('[data-viz-period]');
    bands.forEach((band) => {
        band.addEventListener('pointerenter', () => showTooltip(chart, band));
        band.addEventListener('pointermove', () => showTooltip(chart, band));
        band.addEventListener('focus', () => showTooltip(chart, band));
        band.addEventListener('pointerleave', () => hideTooltip(chart));
        band.addEventListener('blur', () => hideTooltip(chart));
    });

    chart.addEventListener('pointerleave', () => hideTooltip(chart));
}

export function initCharts(root = document) {
    root.querySelectorAll('[data-viz-chart]').forEach(initChart);
}

export function init() {
    initCharts(document);

    // The chart re-renders as an HTMX partial (the week/month toggle), so the
    // swapped-in markup needs wiring up again.
    document.body.addEventListener('htmx:afterSwap', (event) => {
        const target = event.detail && event.detail.target;
        if (!target) return;
        if (target.matches && target.matches('[data-viz-chart]')) {
            initChart(target);
        } else {
            initCharts(target);
        }
    });
}

export default { init, initCharts };
